import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress, Alert,
  Tabs, Tab, Button, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel,
  IconButton, Tooltip, LinearProgress, Divider, Stack,
  TextField, Switch, FormControlLabel, Snackbar, Skeleton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ReplayIcon from '@mui/icons-material/Replay';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  getIntegrationHealth, getIntegrationStats, getIntegrationLogs,
  retryFailedJobs, syncAllItems, syncAllLocations, syncFullMasterData,
  syncSalesOrders, syncPurchaseOrders, getSyncStatus, getMonitoringAlerts,
  getNetsuiteConfig, saveNetsuiteConfig, testNetsuiteConnection,
  exportProductionPerformance,
} from '../../services/erpApi';

interface TabPanelProps { children?: React.ReactNode; value: number; index: number; }
const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>{value === index && children}</Box>
);

const StatusChip = ({ status }: { status: string }) => {
  const map: Record<string, { color: any; icon: React.ReactNode }> = {
    healthy: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    ok: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    success: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    completed: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    degraded: { color: 'warning', icon: <WarningIcon fontSize="small" /> },
    pending: { color: 'warning', icon: <WarningIcon fontSize="small" /> },
    processing: { color: 'info', icon: <SyncIcon fontSize="small" /> },
    failed: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
    error: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
    unhealthy: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
  };
  const cfg = map[status?.toLowerCase()] || { color: 'default', icon: null };
  return <Chip label={status} color={cfg.color} size="small" icon={cfg.icon as any} />;
};

const KPI = ({ label, value, sub, color }: { label: string; value: any; sub?: string; color?: string }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 'bold', color: color || 'text.primary' }}>
        {value ?? '—'}
      </Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

// ── Default / blank config shape ─────────────────────────────────────────────
const BLANK_CONFIG = {
  accountId: '',
  consumerKey: '',
  consumerSecret: '',
  tokenId: '',
  tokenSecret: '',
  realm: 'sandbox',
  webhookSecret: '',
  mesApiKey: '',
  autoSync: false,
  syncInterval: '60',
  syncDirection: 'bidirectional',
};

function generateHex32(): string {
  const arr = new Uint8Array(32);
  window.crypto.getRandomValues(arr);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function defaultDateRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

type SyncStatusValue = 'idle' | 'running' | 'completed' | 'failed';

interface AlertItem {
  alertId: string;
  timestamp: string | Date;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
}

// Sync operations config ──────────────────────────────────────────────────────
const SYNC_OPS = [
  {
    label: 'Sync All Items',
    description: 'Push MES master data items to NetSuite inventory items.',
    fn: syncAllItems,
    key: 'items',
    syncType: 'items',
    direction: 'mes-to-netsuite' as const,
  },
  {
    label: 'Sync Locations',
    description: 'Push MES location hierarchy to NetSuite locations.',
    fn: syncAllLocations,
    key: 'locations',
    syncType: 'locations',
    direction: 'mes-to-netsuite' as const,
  },
  {
    label: 'Full Master Data Sync',
    description: 'Run a complete sync of all master data (items + locations).',
    fn: syncFullMasterData,
    key: 'full',
    syncType: 'full',
    color: 'primary' as const,
    direction: 'mes-to-netsuite' as const,
  },
  {
    label: 'Pull Sales Orders',
    description: 'Import open sales orders from NetSuite into MES.',
    fn: syncSalesOrders,
    key: 'sales-orders',
    syncType: 'sales-orders',
    direction: 'netsuite-to-mes' as const,
  },
  {
    label: 'Pull Purchase Orders',
    description: 'Import open purchase orders from NetSuite into MES.',
    fn: syncPurchaseOrders,
    key: 'purchase-orders',
    syncType: 'purchase-orders',
    direction: 'netsuite-to-mes' as const,
  },
];

const MAX_POLL_INTERVALS = 100; // 100 × 3s = 5 min

const ERPIntegration: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [health, setHealth] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [logType, setLogType] = useState('');
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncMsg, setSyncMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // ── Monitoring Alerts state ───────────────────────────────────────────────
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const alertsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Sync status polling state ─────────────────────────────────────────────
  const [syncStatus, setSyncStatus] = useState<Record<string, SyncStatusValue>>({});
  const [syncErrors, setSyncErrors] = useState<Record<string, string>>({});
  const syncIntervalRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const syncPollCounts = useRef<Record<string, number>>({});

  // ── Production Performance Export state ───────────────────────────────────
  const [exportDateRange, setExportDateRange] = useState(defaultDateRange);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSnack, setExportSnack] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);

  // ── Configuration tab state ───────────────────────────────────────────────
  const [config, setConfig] = useState<typeof BLANK_CONFIG>(BLANK_CONFIG);
  const [savedConfig, setSavedConfig] = useState<typeof BLANK_CONFIG>(BLANK_CONFIG);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configTesting, setConfigTesting] = useState(false);
  const [configSnack, setConfigSnack] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const stopSyncPolling = useCallback((key: string) => {
    if (syncIntervalRefs.current[key]) {
      clearInterval(syncIntervalRefs.current[key]);
      delete syncIntervalRefs.current[key];
      delete syncPollCounts.current[key];
    }
  }, []);

  // ── Monitoring Alerts fetch ───────────────────────────────────────────────
  const fetchAlerts = useCallback(async () => {
    try {
      const res = await getMonitoringAlerts();
      setAlerts((res?.data as AlertItem[]) || []);
    } catch {
      // keep existing alerts on transient failure
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    alertsIntervalRef.current = setInterval(fetchAlerts, 60_000);
    return () => {
      if (alertsIntervalRef.current) clearInterval(alertsIntervalRef.current);
    };
  }, [fetchAlerts]);

  const dismissAlert = (alertId: string) => {
    setAlerts((prev) => prev.filter((a) => a.alertId !== alertId));
  };

  // ── Sync polling ──────────────────────────────────────────────────────────
  const startSyncPolling = useCallback((key: string, syncType: string) => {
    syncPollCounts.current[key] = 0;
    setSyncStatus((prev) => ({ ...prev, [key]: 'running' }));
    setSyncErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });

    const intervalId = setInterval(async () => {
      syncPollCounts.current[key] = (syncPollCounts.current[key] || 0) + 1;

      if (syncPollCounts.current[key] > MAX_POLL_INTERVALS) {
        stopSyncPolling(key);
        setSyncStatus((prev) => ({ ...prev, [key]: 'failed' }));
        setSyncErrors((prev) => ({ ...prev, [key]: 'Sync taking longer than expected — check logs' }));
        return;
      }

      try {
        const res = await getSyncStatus(syncType);
        const status: string = res?.data?.status || 'running';

        if (status === 'completed') {
          stopSyncPolling(key);
          setSyncStatus((prev) => ({ ...prev, [key]: 'completed' }));
          setTimeout(() => setSyncStatus((prev) => ({ ...prev, [key]: 'idle' })), 5000);
        } else if (status === 'failed') {
          stopSyncPolling(key);
          setSyncStatus((prev) => ({ ...prev, [key]: 'failed' }));
          setSyncErrors((prev) => ({ ...prev, [key]: res?.data?.error || 'Sync failed' }));
        }
        // else still running — keep polling
      } catch {
        // transient — keep polling
      }
    }, 3000);

    syncIntervalRefs.current[key] = intervalId;
  }, [stopSyncPolling]);

  const runSync = useCallback(async (key: string, syncType: string, fn: () => Promise<any>) => {
    setSyncMsg(null);
    setSyncStatus((prev) => ({ ...prev, [key]: 'running' }));
    try {
      await fn();
      startSyncPolling(key, syncType);
    } catch {
      setSyncStatus((prev) => ({ ...prev, [key]: 'failed' }));
      setSyncErrors((prev) => ({ ...prev, [key]: 'Failed to initiate sync' }));
    }
  }, [startSyncPolling]);

  // Cleanup all polling on unmount
  useEffect(() => {
    return () => {
      Object.keys(syncIntervalRefs.current).forEach(stopSyncPolling);
    };
  }, [stopSyncPolling]);

  // ── Main data load ────────────────────────────────────────────────────────
  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const data = await getNetsuiteConfig();
      const merged = { ...BLANK_CONFIG, ...data };
      setConfig(merged);
      setSavedConfig(merged);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setConfig(BLANK_CONFIG);
        setSavedConfig(BLANK_CONFIG);
      } else {
        setConfigSnack({ text: 'Failed to load configuration', severity: 'error' });
      }
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const handleConfigChange = (field: keyof typeof BLANK_CONFIG) =>
    (e: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      const value = (e.target as any).type === 'checkbox'
        ? (e.target as any).checked
        : (e.target as any).value;
      setConfig((prev) => ({ ...prev, [field]: value }));
    };

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    try {
      await saveNetsuiteConfig(config);
      setSavedConfig(config);
      setConfigSnack({ text: 'Configuration saved successfully', severity: 'success' });
    } catch {
      setConfigSnack({ text: 'Failed to save configuration', severity: 'error' });
    } finally {
      setConfigSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setConfigTesting(true);
    try {
      await testNetsuiteConnection();
      setConfigSnack({ text: 'Connection test successful', severity: 'success' });
    } catch {
      setConfigSnack({ text: 'Connection test failed', severity: 'error' });
    } finally {
      setConfigTesting(false);
    }
  };

  const handleResetConfig = () => {
    setConfig(savedConfig);
  };

  const loadMain = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, s] = await Promise.all([
        getIntegrationHealth().catch(() => null),
        getIntegrationStats().catch(() => null),
      ]);
      setHealth(h);
      setStats(s?.data);
    } catch {
      setError('Failed to load integration data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await getIntegrationLogs(logType || undefined, 200);
      setLogs(res?.data || []);
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [logType]);

  useEffect(() => { loadMain(); }, [loadMain]);
  useEffect(() => { if (tab === 2) loadLogs(); }, [tab, loadLogs]);
  useEffect(() => { if (tab === 4) loadConfig(); }, [tab, loadConfig]);

  const handleRetryFailed = async () => {
    try {
      await retryFailedJobs();
      setSyncMsg({ text: 'Retry of failed jobs initiated', type: 'success' });
      loadMain();
    } catch {
      setSyncMsg({ text: 'Retry failed', type: 'error' });
    }
  };

  // ── Production Performance Export ─────────────────────────────────────────
  const handleExportPerformance = async () => {
    setExportLoading(true);
    try {
      await exportProductionPerformance(exportDateRange.from, exportDateRange.to);
      setExportSnack({ text: 'Production performance exported to NetSuite', severity: 'success' });
    } catch {
      setExportSnack({ text: 'Failed to export production performance', severity: 'error' });
    } finally {
      setExportLoading(false);
    }
  };

  const syncDirection = savedConfig.syncDirection;
  const canExportPerformance =
    syncDirection === 'bidirectional' || syncDirection === 'mes_to_netsuite';

  const logStats = stats?.logs;
  const queueStats = stats?.queue;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h5">ERP Integration</Typography>
          <Typography variant="body2" color="text.secondary">NetSuite ↔ MES synchronisation</Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh">
          <IconButton onClick={loadMain} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {syncMsg && (
        <Alert severity={syncMsg.type} sx={{ mb: 2 }} onClose={() => setSyncMsg(null)}>
          {syncMsg.text}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tab label="Overview" />
        <Tab label="Sync Controls" />
        <Tab label="Integration Logs" />
        <Tab label="Health" />
        <Tab label="Configuration" />
      </Tabs>

      {/* ── OVERVIEW ── */}
      <TabPanel value={tab} index={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* Health banner */}
            {health && (
              <Card sx={{ mb: 3, borderLeft: 4, borderColor: health.status === 'healthy' ? 'success.main' : 'warning.main' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <StatusChip status={health.status} />
                  <Typography variant="body1">
                    NetSuite Integration — {health.status === 'healthy' ? 'All systems operational' : 'Some services degraded'}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {health.timestamp ? new Date(health.timestamp).toLocaleString() : ''}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Log KPIs */}
            {logStats && (
              <>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Integration Activity</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <KPI label="Total Jobs" value={logStats.total} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KPI label="Successful" value={logStats.success} color="success.main" />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KPI label="Failed" value={logStats.failed}
                      color={logStats.failed > 0 ? 'error.main' : 'text.primary'} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KPI label="Pending / Processing"
                      value={`${logStats.pending ?? 0} / ${logStats.processing ?? 0}`} />
                  </Grid>
                </Grid>
              </>
            )}

            {/* Queue KPIs */}
            {queueStats && (
              <>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Queue Status</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <KPI label="Waiting" value={queueStats.waiting ?? 0} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KPI label="Active" value={queueStats.active ?? 0} color="info.main" />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KPI label="Completed" value={queueStats.completed ?? 0} color="success.main" />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KPI label="Failed" value={queueStats.failed ?? 0}
                      color={queueStats.failed > 0 ? 'error.main' : 'text.primary'} />
                  </Grid>
                </Grid>
                {queueStats.failed > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Button variant="outlined" color="warning" startIcon={<ReplayIcon />}
                      onClick={handleRetryFailed}>
                      Retry {queueStats.failed} Failed Jobs
                    </Button>
                  </Box>
                )}
              </>
            )}

            {!logStats && !queueStats && !error && (
              <Alert severity="info">No integration data available. Check that the NetSuite service is running.</Alert>
            )}

            {/* ── Monitoring Alerts ── */}
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Monitoring Alerts</Typography>

              {alertsLoading ? (
                <Stack spacing={1}>
                  <Skeleton variant="rectangular" height={48} />
                  <Skeleton variant="rectangular" height={48} />
                </Stack>
              ) : alerts.length === 0 ? (
                <Chip
                  label="No active alerts"
                  color="success"
                  icon={<CheckCircleIcon />}
                  sx={{ fontWeight: 500 }}
                />
              ) : (
                <Stack spacing={1}>
                  {alerts.map((alert) => {
                    const severityMap: Record<string, 'error' | 'warning' | 'info'> = {
                      critical: 'error',
                      warning: 'warning',
                      info: 'info',
                    };
                    const muiSeverity = severityMap[alert.severity] || 'info';
                    return (
                      <Alert
                        key={alert.alertId}
                        severity={muiSeverity}
                        onClose={() => dismissAlert(alert.alertId)}
                        action={
                          <Button
                            color="inherit"
                            size="small"
                            onClick={() => dismissAlert(alert.alertId)}
                          >
                            Dismiss
                          </Button>
                        }
                      >
                        <strong>{alert.title}</strong> — {alert.message}
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                          {alert.timestamp
                            ? new Date(alert.timestamp).toLocaleString('en-KE')
                            : ''}
                        </Typography>
                      </Alert>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </>
        )}
      </TabPanel>

      {/* ── SYNC CONTROLS ── */}
      <TabPanel value={tab} index={1}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manually trigger synchronisation operations between MES and NetSuite.
        </Typography>
        <Grid container spacing={2}>
          {SYNC_OPS.map((op) => {
            const currentStatus = syncStatus[op.key] || 'idle';
            const isRunning = currentStatus === 'running';
            const isCompleted = currentStatus === 'completed';
            const isFailed = currentStatus === 'failed';
            const opError = syncErrors[op.key];

            // Direction-based disable logic
            let dirDisabled = false;
            let dirTooltip = '';
            if (syncDirection === 'mes_to_netsuite' && op.direction === 'netsuite-to-mes') {
              dirDisabled = true;
              dirTooltip = 'Not available with current sync direction';
            } else if (syncDirection === 'netsuite_to_mes' && op.direction === 'mes-to-netsuite') {
              dirDisabled = true;
              dirTooltip = 'Not available with current sync direction';
            }

            const btnDisabled = isRunning || dirDisabled;

            return (
              <Grid item xs={12} sm={6} md={4} key={op.key}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600}>{op.label}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                      {op.description}
                    </Typography>
                    <Tooltip title={dirDisabled ? dirTooltip : ''} disableHoverListener={!dirDisabled}>
                      <span>
                        <Button
                          variant={op.color === 'primary' ? 'contained' : 'outlined'}
                          color={op.color || 'primary'}
                          startIcon={isRunning ? <CircularProgress size={16} /> : <SyncIcon />}
                          onClick={() => runSync(op.key, op.syncType, op.fn)}
                          disabled={btnDisabled}
                          fullWidth
                        >
                          {isRunning ? 'Running...' : op.label}
                        </Button>
                      </span>
                    </Tooltip>

                    {isRunning && (
                      <LinearProgress sx={{ mt: 1 }} />
                    )}

                    {isCompleted && (
                      <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                        ✓ Sync completed
                      </Typography>
                    )}

                    {isFailed && (
                      <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                        ✗ {opError || 'Sync failed'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* ── Export Production Performance ── */}
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Export Production Performance</Typography>

          <Tooltip
            title={!canExportPerformance ? 'Requires bidirectional or MES-to-NetSuite sync direction' : ''}
            disableHoverListener={canExportPerformance}
          >
            <span>
              <Card sx={{ opacity: canExportPerformance ? 1 : 0.55 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Export MES production performance data to NetSuite for the selected date range.
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="From"
                        type="date"
                        size="small"
                        fullWidth
                        value={exportDateRange.from}
                        onChange={(e) =>
                          setExportDateRange((prev) => ({ ...prev, from: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        disabled={!canExportPerformance}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="To"
                        type="date"
                        size="small"
                        fullWidth
                        value={exportDateRange.to}
                        onChange={(e) =>
                          setExportDateRange((prev) => ({ ...prev, to: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        disabled={!canExportPerformance}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={exportLoading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                        onClick={handleExportPerformance}
                        disabled={!canExportPerformance || exportLoading}
                      >
                        {exportLoading ? 'Exporting...' : 'Export to NetSuite'}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </span>
          </Tooltip>
        </Box>
      </TabPanel>

      {/* ── INTEGRATION LOGS ── */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select value={logType} label="Type" onChange={(e) => setLogType(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ITEM_SYNC">Item Sync</MenuItem>
              <MenuItem value="WORK_ORDER_SYNC">Work Order Sync</MenuItem>
              <MenuItem value="INVENTORY_TRANSACTION">Inventory Transaction</MenuItem>
              <MenuItem value="PRODUCTION_COMPLETION">Production Completion</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadLogs} disabled={logsLoading}>
            Refresh
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">{logs.length} records</Typography>
        </Box>

        {logsLoading ? (
          <LinearProgress />
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 550 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>External ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>MES ID</TableCell>
                  <TableCell>NetSuite ID</TableCell>
                  <TableCell>Attempts</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No integration logs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : logs.map((log, i) => (
                  <TableRow key={log.id || i} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{log.externalId}</TableCell>
                    <TableCell>
                      <Chip label={log.integrationType || log.type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell><StatusChip status={log.status} /></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{log.mesEntityId || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{log.netsuiteId || '—'}</TableCell>
                    <TableCell align="center">{log.attempts ?? 0}</TableCell>
                    <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      {log.createdAt ? new Date(log.createdAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'error.main', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.errorMessage || ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* ── HEALTH ── */}
      <TabPanel value={tab} index={3}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : health ? (
          <>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <StatusChip status={health.status} />
              <Typography variant="h6">System Health</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Last checked: {health.timestamp ? new Date(health.timestamp).toLocaleString() : 'unknown'}
              </Typography>
            </Stack>

            {health.services && Object.entries(health.services).map(([name, svc]: [string, any]) => (
              <Card key={name} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <StatusChip status={svc.status} />
                    <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                      {name.replace(/_/g, ' ')}
                    </Typography>
                    {svc.responseTime != null && (
                      <Chip label={`${svc.responseTime}ms`} size="small" variant="outlined" />
                    )}
                  </Box>
                  {svc.message && (
                    <Typography variant="body2" color="text.secondary">{svc.message}</Typography>
                  )}
                  {svc.details && (
                    <Typography variant="caption" color="text.secondary" component="pre" sx={{ mt: 1, display: 'block', fontSize: 11 }}>
                      {JSON.stringify(svc.details, null, 2)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}

            {!health.services && (
              <Alert severity="info">Detailed service health not available.</Alert>
            )}
          </>
        ) : (
          <Alert severity="warning">Health data unavailable. The NetSuite integration service may be offline.</Alert>
        )}
      </TabPanel>

      {/* ── CONFIGURATION ── */}
      <TabPanel value={tab} index={4}>
        {configLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={3}>

            {/* Connection Settings */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Connection Settings</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="NetSuite Account ID"
                        value={config.accountId}
                        onChange={handleConfigChange('accountId')}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Consumer Key"
                        value={config.consumerKey}
                        onChange={handleConfigChange('consumerKey')}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Consumer Secret"
                        type="password"
                        value={config.consumerSecret}
                        onChange={handleConfigChange('consumerSecret')}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Token ID"
                        value={config.tokenId}
                        onChange={handleConfigChange('tokenId')}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Token Secret"
                        type="password"
                        value={config.tokenSecret}
                        onChange={handleConfigChange('tokenSecret')}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Realm / Environment</InputLabel>
                        <Select
                          value={config.realm}
                          label="Realm / Environment"
                          onChange={handleConfigChange('realm') as any}
                        >
                          <MenuItem value="sandbox">Sandbox</MenuItem>
                          <MenuItem value="production">Production</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Webhook Settings */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Webhook Settings</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                          label="Webhook Secret"
                          type="password"
                          value={config.webhookSecret}
                          onChange={handleConfigChange('webhookSecret')}
                          fullWidth
                          size="small"
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 0.25, whiteSpace: 'nowrap', flexShrink: 0 }}
                          onClick={() => setConfig((prev) => ({ ...prev, webhookSecret: generateHex32() }))}
                        >
                          Generate
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                          label="MES Internal API Key"
                          type="password"
                          value={config.mesApiKey}
                          onChange={handleConfigChange('mesApiKey')}
                          fullWidth
                          size="small"
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 0.25, whiteSpace: 'nowrap', flexShrink: 0 }}
                          onClick={() => setConfig((prev) => ({ ...prev, mesApiKey: generateHex32() }))}
                        >
                          Generate
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Sync Settings */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Sync Settings</Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.autoSync}
                            onChange={handleConfigChange('autoSync')}
                          />
                        }
                        label="Auto-sync enabled"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Sync Interval</InputLabel>
                        <Select
                          value={config.syncInterval}
                          label="Sync Interval"
                          onChange={handleConfigChange('syncInterval') as any}
                          disabled={!config.autoSync}
                        >
                          <MenuItem value="15">Every 15 min</MenuItem>
                          <MenuItem value="30">Every 30 min</MenuItem>
                          <MenuItem value="60">Every 1 hour</MenuItem>
                          <MenuItem value="240">Every 4 hours</MenuItem>
                          <MenuItem value="1440">Daily</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Sync Direction</InputLabel>
                        <Select
                          value={config.syncDirection}
                          label="Sync Direction"
                          onChange={handleConfigChange('syncDirection') as any}
                        >
                          <MenuItem value="bidirectional">Bidirectional</MenuItem>
                          <MenuItem value="netsuite_to_mes">NetSuite→MES only</MenuItem>
                          <MenuItem value="mes_to_netsuite">MES→NetSuite only</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveConfig}
                  disabled={configSaving}
                  startIcon={configSaving ? <CircularProgress size={16} /> : undefined}
                >
                  {configSaving ? 'Saving...' : 'Save Configuration'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleTestConnection}
                  disabled={configTesting}
                  startIcon={configTesting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                >
                  {configTesting ? 'Testing...' : 'Test Connection'}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleResetConfig}
                  disabled={configSaving || configTesting}
                >
                  Reset
                </Button>
              </Stack>
            </Grid>

          </Grid>
        )}
      </TabPanel>

      {/* Configuration Snackbar */}
      <Snackbar
        open={!!configSnack}
        autoHideDuration={4000}
        onClose={() => setConfigSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setConfigSnack(null)}
          severity={configSnack?.severity}
          sx={{ width: '100%' }}
        >
          {configSnack?.text}
        </Alert>
      </Snackbar>

      {/* Export Snackbar */}
      <Snackbar
        open={!!exportSnack}
        autoHideDuration={4000}
        onClose={() => setExportSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setExportSnack(null)}
          severity={exportSnack?.severity}
          sx={{ width: '100%' }}
        >
          {exportSnack?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ERPIntegration;
